import React, { useState, useEffect } from 'react';
import { X, Globe, Edit3 } from 'lucide-react';
import { api } from '../../lib/api';
import type { Meeting } from '@sahaj/types';

interface EditMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  meeting: Meeting | null;
  onUpdated: () => void;
}

export const EditMeetingModal: React.FC<EditMeetingModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  meeting,
  onUpdated,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && meeting) {
      setTitle(meeting.title || '');
      setDescription(meeting.description || '');
      setTimezone(meeting.timezone || 'UTC');

      if (meeting.startTime) {
        const startObj = new Date(meeting.startTime);
        setStartDate(startObj.toISOString().split('T')[0] || '');
        setStartTime(startObj.toTimeString().slice(0, 5) || '');
      }

      if (meeting.endTime) {
        const endObj = new Date(meeting.endTime);
        setEndDate(endObj.toISOString().split('T')[0] || '');
        setEndTime(endObj.toTimeString().slice(0, 5) || '');
      }

      setError(null);
    }
  }, [isOpen, meeting]);

  if (!isOpen || !meeting) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Meeting title is required');
      return;
    }

    let startIso: string | undefined = undefined;
    let endIso: string | undefined = undefined;

    if (startDate && startTime && endDate && endTime) {
      const startObj = new Date(`${startDate}T${startTime}`);
      const endObj = new Date(`${endDate}T${endTime}`);

      if (isNaN(startObj.getTime()) || isNaN(endObj.getTime())) {
        setError('Invalid start or end date/time');
        return;
      }

      if (endObj <= startObj) {
        setError('Meeting end time must be after start time');
        return;
      }

      startIso = startObj.toISOString();
      endIso = endObj.toISOString();
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload: any = {
        title: title.trim(),
        timezone,
      };
      if (description.trim()) payload.description = description.trim();
      if (startIso) payload.startTime = startIso;
      if (endIso) payload.endTime = endIso;

      await api.meetings.update(workspaceId, meeting.id, payload);

      onUpdated();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to update meeting');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-card">
          <div className="flex items-center gap-2">
            <Edit3 className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Edit Meeting</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 text-xs bg-destructive/15 text-destructive rounded-lg border border-destructive/20">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">
              Title *
            </label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-muted/40 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">
              Description
            </label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-muted/40 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          <div className="space-y-3 p-3 bg-muted/20 border border-border rounded-lg">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-muted-foreground mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-xs bg-card border border-border rounded-md"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-muted-foreground mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-xs bg-card border border-border rounded-md"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-muted-foreground mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-xs bg-card border border-border rounded-md"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-muted-foreground mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-xs bg-card border border-border rounded-md"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                <Globe className="w-3 h-3" /> Timezone
              </label>
              <input
                type="text"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs bg-card border border-border rounded-md"
              />
            </div>
          </div>

          <div className="pt-3 border-t border-border flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg transition shadow-sm disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
